import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Trash,
  Camera,
  Stack,
  Printer,
  Rows,
  SquaresFour,
  X,
  PencilSimple,
  UploadSimple,
  LockSimple,
  LockSimpleOpen,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PieceLabel } from "@/components/PieceLabel";
import { PhotoGallery } from "@/components/PhotoGallery";
import { PhotoEditor } from "@/components/PhotoEditor";
import { PieceHoldDialog } from "@/components/PieceHoldDialog";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { shrinkAll, shrinkForUpload } from "@/lib/shrinkImage";

const STATUSES = ["Ordered", "Received", "In Stock", "Reserved", "Consumed"];
const STATUS_STYLE = {
  Ordered: "border-sky-500/25 bg-sky-500/10 text-sky-400",
  Received: "border-emerald-500/25 bg-emerald-500/10 text-emerald-400",
  "In Stock": "border-emerald-500/25 bg-emerald-500/10 text-emerald-400",
  Reserved: "border-amber-500/25 bg-amber-500/10 text-amber-400",
  Consumed: "border-zinc-500/25 bg-zinc-500/10 text-zinc-400",
};

const EMPTY_BUNDLE = {
  count: "5",
  bundle_number: "",
  lot_number: "",
  name: "",
  serial_prefix: "",
  serial_start: "1",
  length_in: "",
  width_in: "",
};

const EMPTY = {
  name: "",
  serial_number: "",
  lot_number: "",
  bundle_number: "",
  length_in: "",
  width_in: "",
  notes: "",
};

const photoSrc = (p) =>
  `${API}/photos/${p.storage_path}?auth=${localStorage.getItem("sw_token")}`;

const livePhotos = (p) => (p.photos || []).filter((ph) => !ph.is_deleted);
const HOLDABLE = ["In Stock", "Received"];

const sqftOf = (p, slab) => {
  const l = Number(p.length_in) || Number(slab.length_in) || 0;
  const w = Number(p.width_in) || Number(slab.width_in) || 0;
  return Math.round(((l * w) / 144) * 10) / 10;
};

export const SlabPieces = ({ slab, onChange }) => {
  const navigate = useNavigate();
  const [view, setView] = useState(
    () => localStorage.getItem("bg_pieces_view") || "list",
  );
  const [adding, setAdding] = useState(false);
  const [bundling, setBundling] = useState(false);
  const [bundleDraft, setBundleDraft] = useState(EMPTY_BUNDLE);
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploadFor, setUploadFor] = useState(null);
  const [printing, setPrinting] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editDraft, setEditDraft] = useState(EMPTY);
  const [pendingEdit, setPendingEdit] = useState(null);
  const [holdFor, setHoldFor] = useState(null);
  const fileRef = useRef(null);
  const libraryRef = useRef(null);
  const pieces = slab.pieces || [];
  const pickFor = (pid, ref) => {
    setUploadFor(pid);
    ref.current?.click();
  };
  const setPiecesView = (v) => {
    setView(v);
    localStorage.setItem("bg_pieces_view", v);
  };
  const coverOf = (p) => livePhotos(p)[0] || livePhotos(slab)[0];

  const pieceCall = async (req, okMsg, errMsg) => {
    try {
      const { data } = await req();
      onChange(data);
      if (okMsg) toast.success(okMsg);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || errMsg);
    }
  };
  const removePhoto = (pid, photoId) =>
    pieceCall(
      () => api.delete(`/slabs/${slab.id}/pieces/${pid}/photos/${photoId}`),
      "Photo removed",
      "Could not remove photo",
    );
  const reorderPhotos = (pid, order) =>
    pieceCall(
      () => api.put(`/slabs/${slab.id}/pieces/${pid}/photos/order`, { order }),
      null,
      "Could not reorder photos",
    );
  const toggleWebsite = (pid, ph) =>
    pieceCall(
      () =>
        api.put(`/slabs/${slab.id}/pieces/${pid}/photos/${ph.id}/website`, {
          show: ph.show_on_website === false,
        }),
      ph.show_on_website === false
        ? "Photo shown on the website"
        : "Photo hidden from the website",
      "Could not update photo",
    );
  const revertHd = (pid, ph) =>
    pieceCall(
      () =>
        api.post("/photos/revert-hd", {
          collection: "slabs",
          doc_id: slab.id,
          photo_id: ph.id,
          piece_id: pid,
        }),
      "Original photo restored",
      "Could not undo HD",
    );
  const editPhoto = async (pid, ph) => {
    try {
      const { data } = await api.get(`/photos/${ph.storage_path}`, {
        responseType: "blob",
      });
      setPendingEdit({
        pieceId: pid,
        photoId: ph.id,
        file: new File([data], ph.original_filename || "photo.jpg", {
          type: data.type || "image/jpeg",
        }),
      });
    } catch {
      toast.error("Could not open that photo");
    }
  };
  const saveEdited = async (file) => {
    const { pieceId, photoId } = pendingEdit;
    setPendingEdit(null);
    file = await shrinkForUpload(file);
    const fd = new FormData();
    fd.append("file", file);
    if (file.enhanced) fd.append("enhanced", "true");
    await pieceCall(
      () => api.post(`/slabs/${slab.id}/pieces/${pieceId}/photos/${photoId}`, fd),
      "Photo updated",
      "Could not save the edited photo",
    );
  };
  const release = (pid) =>
    pieceCall(
      () => api.post(`/slabs/${slab.id}/pieces/${pid}/release`),
      "Piece released back to stock",
      "Could not release piece",
    );

  const setStatus = async (pid, status) => {
    try {
      const { data } = await api.patch(
        `/slabs/${slab.id}/pieces/${pid}/status`,
        { status },
      );
      onChange(data);
      toast.success(`Piece → ${status}`);
    } catch {
      toast.error("Could not change piece status");
    }
  };

  const printLabels = (list) => {
    setPrinting(list);
    setTimeout(() => {
      document.body.classList.add("printing-piece");
      window.print();
      document.body.classList.remove("printing-piece");
    }, 300);
  };

  const printBundle = (piece) =>
    printLabels(
      piece.bundle_number
        ? pieces.filter((x) => x.bundle_number === piece.bundle_number)
        : [piece],
    );

  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/slabs/${slab.id}/pieces`, {
        ...draft,
        length_in: Number(draft.length_in) || 0,
        width_in: Number(draft.width_in) || 0,
      });
      onChange(data);
      setDraft(EMPTY);
      setAdding(false);
      toast.success("Piece added");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not add piece",
      );
    }
    setBusy(false);
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setEditDraft({
      name: p.name || "",
      serial_number: p.serial_number || "",
      lot_number: p.lot_number || "",
      bundle_number: p.bundle_number || "",
      length_in: p.length_in ?? "",
      width_in: p.width_in ?? "",
      notes: p.notes || "",
    });
  };

  const saveEdit = async (pid) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/slabs/${slab.id}/pieces/${pid}`, {
        ...editDraft,
        length_in: Number(editDraft.length_in) || 0,
        width_in: Number(editDraft.width_in) || 0,
      });
      onChange(data);
      setEditing(null);
      toast.success("Piece updated");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not update piece",
      );
    }
    setBusy(false);
  };

  const editFields = (pid) => (
    <div
      className="mt-3 grid gap-2 rounded-md border border-zinc-800 bg-zinc-900 p-3 lg:grid-cols-3"
      data-testid={`piece-edit-form-${pid}`}
    >
      {[
        ["name", "Name"],
        ["serial_number", "Serial #"],
        ["lot_number", "Lot #"],
        ["bundle_number", "Bundle #"],
        ["length_in", "Length (in)"],
        ["width_in", "Width (in)"],
      ].map(([k, label]) => (
        <div key={k} className="space-y-1">
          <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
            {label}
          </span>
          <Input
            data-testid={`piece-edit-${k}-${pid}`}
            type={k.endsWith("_in") ? "number" : "text"}
            value={editDraft[k]}
            onChange={(e) =>
              setEditDraft({ ...editDraft, [k]: e.target.value })
            }
            className="h-11 border-zinc-800 bg-zinc-950"
          />
        </div>
      ))}
      <div className="flex items-end gap-2 lg:col-span-3">
        <Button
          data-testid={`piece-edit-save-${pid}`}
          disabled={busy}
          onClick={() => saveEdit(pid)}
          className="h-10 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
        >
          {busy ? "Saving…" : "Save"}
        </Button>
        <Button
          data-testid={`piece-edit-cancel-${pid}`}
          variant="outline"
          onClick={() => setEditing(null)}
          className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
        >
          Cancel
        </Button>
      </div>
    </div>
  );

  const addBundle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/slabs/${slab.id}/pieces/bulk`, {
        count: Number(bundleDraft.count) || 1,
        bundle_number: bundleDraft.bundle_number,
        lot_number: bundleDraft.lot_number,
        name: bundleDraft.name || slab.slab_color || "",
        serial_prefix: bundleDraft.serial_prefix,
        serial_start: Number(bundleDraft.serial_start) || 1,
        length_in: Number(bundleDraft.length_in) || 0,
        width_in: Number(bundleDraft.width_in) || 0,
      });
      onChange(data);
      setBundling(false);
      setBundleDraft(EMPTY_BUNDLE);
      toast.success(`${bundleDraft.count} pieces added`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not add bundle",
      );
    }
    setBusy(false);
  };

  const remove = async (pid) => {
    const { data } = await api.delete(`/slabs/${slab.id}/pieces/${pid}`);
    onChange(data);
    toast.success("Piece removed");
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !uploadFor) return;
    setBusy(true);
    let failed = 0;
    for (const file of await shrinkAll(files)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post(
          `/slabs/${slab.id}/pieces/${uploadFor}/photos`,
          fd,
        );
        onChange(data);
      } catch {
        failed += 1;
      }
    }
    const ok = files.length - failed;
    if (ok) toast.success(ok === 1 ? "Photo added to piece" : `${ok} photos added to piece`);
    if (failed) toast.error(`${failed} photo${failed === 1 ? "" : "s"} failed to upload`);
    setBusy(false);
    setUploadFor(null);
  };

  const holdButton = (p, compact) => {
    const cls = compact
      ? "h-8 px-2.5 text-[10px] uppercase tracking-widest"
      : "h-9 px-2.5 text-[10px] uppercase tracking-widest sm:h-10 sm:px-3 sm:text-xs";
    if (p.status === "Reserved" && p.reserved_job && !p.reservation_id) return null;
    if (p.status === "Reserved")
      return (
        <Button
          data-testid={`piece-release-button-${p.id}`}
          variant="outline"
          disabled={busy}
          onClick={() => release(p.id)}
          title={`Release this piece back to stock${p.reserved_for ? ` (held for ${p.reserved_for})` : ""}`}
          className={`${cls} border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20`}
        >
          <LockSimpleOpen size={compact ? 12 : 14} weight="bold" className="mr-1" />{" "}
          Release
        </Button>
      );
    if (!HOLDABLE.includes(p.status || "In Stock")) return null;
    return (
      <Button
        data-testid={`piece-hold-button-${p.id}`}
        variant="outline"
        disabled={busy}
        onClick={() => setHoldFor(p)}
        title="Put this piece on hold for a customer"
        className={`${cls} border-amber-500/40 bg-transparent text-amber-300 hover:bg-amber-500/10`}
      >
        <LockSimple size={compact ? 12 : 14} weight="bold" className="mr-1" /> Hold
      </Button>
    );
  };

  const heldChip = (p) => {
    if (p.status !== "Reserved" || !(p.reserved_for || p.reserved_job)) return null;
    const target = p.reserved_job
      ? p.job_id && `/projects/${p.job_id}?tab=field`
      : p.held_project_id
        ? `/projects/${p.held_project_id}`
        : p.reservation_id && `/reservation/${p.reservation_id}`;
    const label = p.reserved_job
      ? `Job ${p.reserved_job}${p.reserved_for ? ` · ${p.reserved_for}` : ""}`
      : `Held · ${[p.reserved_for, p.held_project_code, p.held_for].filter(Boolean).join(" · ") || p.reservation_tag}`;
    return (
      <button
        type="button"
        data-testid={`piece-held-chip-${p.id}`}
        onClick={() => target && navigate(target)}
        title={target ? (p.reserved_job ? "Open the job" : "Open the hold") : ""}
        className={`rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-amber-300 ${
          target ? "hover:bg-amber-500/20" : "cursor-default"
        }`}
      >
        <LockSimple size={10} weight="bold" className="mr-1 inline-block" />
        {label}
      </button>
    );
  };

  return (
    <div
      className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid="slab-pieces-panel"
    >
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <div className="flex w-full items-center gap-2 sm:mr-auto sm:w-auto">
          <Stack size={16} weight="bold" className="text-indigo-300" />
          <div className="text-xs uppercase tracking-widest text-zinc-500">
            Slabs in Inventory ({pieces.length})
          </div>
        </div>
        <div className="flex overflow-hidden rounded-md border border-zinc-800">
          {[
            ["list", "List", Rows],
            ["grid", "Grid", SquaresFour],
          ].map(([k, label, Icon]) => (
            <button
              key={k}
              data-testid={`pieces-view-${k}`}
              onClick={() => setPiecesView(k)}
              className={`flex h-10 items-center gap-1.5 px-3 text-[10px] font-semibold uppercase tracking-widest transition-colors duration-150 ${
                view === k
                  ? "bg-indigo-500 text-white"
                  : "bg-zinc-950 text-zinc-400 hover:bg-zinc-800"
              }`}
            >
              <Icon size={13} weight="bold" /> {label}
            </button>
          ))}
        </div>
        <Button
          data-testid="print-all-piece-labels"
          variant="outline"
          disabled={pieces.length === 0}
          onClick={() => printLabels(pieces)}
          className="h-10 flex-1 border-indigo-400/40 bg-indigo-500/10 px-3 text-xs uppercase tracking-widest text-indigo-300 hover:bg-indigo-500/20 sm:flex-none"
        >
          <Printer size={14} weight="bold" className="mr-1" /> Print all (
          {pieces.length})
        </Button>
        <Button
          data-testid="add-bundle-button"
          variant="outline"
          onClick={() => {
            if (!bundling)
              setBundleDraft({
                ...EMPTY_BUNDLE,
                name: slab.slab_color || "",
                length_in: slab.length_in || "",
                width_in: slab.width_in || "",
              });
            setBundling(!bundling);
          }}
          className="h-10 flex-1 border-indigo-400/40 bg-indigo-500/10 px-3 text-xs uppercase tracking-widest text-indigo-300 hover:bg-indigo-500/20 sm:flex-none"
        >
          <Stack size={14} weight="bold" className="mr-1" /> Add bundle
        </Button>
        <Button
          data-testid="add-slab-piece-button"
          variant="outline"
          onClick={() => {
            if (!adding) setDraft({ ...EMPTY, name: slab.slab_color || "" });
            setAdding(!adding);
          }}
          className="h-10 flex-1 border-zinc-700 bg-transparent px-3 text-xs uppercase tracking-widest hover:bg-zinc-800 sm:flex-none"
        >
          <Plus size={14} weight="bold" className="mr-1" /> Add piece
        </Button>
      </div>

      {bundling && (
        <div
          className="mt-3 grid gap-2 rounded-md border border-indigo-500/25 bg-zinc-950 p-3 lg:grid-cols-4"
          data-testid="slab-bundle-form"
        >
          {[
            ["count", "How many pieces"],
            ["bundle_number", "Bundle #"],
            ["lot_number", "Lot #"],
            ["name", "Name"],
            ["serial_prefix", "Serial prefix"],
            ["serial_start", "Start at"],
            ["length_in", "Length (in)"],
            ["width_in", "Width (in)"],
          ].map(([k, label]) => (
            <div key={k} className="space-y-1">
              <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                {label}
              </span>
              <Input
                data-testid={`bundle-field-${k}`}
                type={
                  ["count", "serial_start", "length_in", "width_in"].includes(k)
                    ? "number"
                    : "text"
                }
                value={bundleDraft[k]}
                onChange={(e) =>
                  setBundleDraft({ ...bundleDraft, [k]: e.target.value })
                }
                className="h-11 border-zinc-800 bg-zinc-900"
              />
            </div>
          ))}
          <div className="flex items-end gap-2 lg:col-span-4">
            <Button
              data-testid="save-bundle-button"
              disabled={busy}
              onClick={addBundle}
              className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              {busy ? "Creating…" : `Create ${bundleDraft.count || 0} pieces`}
            </Button>
            <Button
              data-testid="cancel-bundle-button"
              variant="outline"
              onClick={() => setBundling(false)}
              className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
            >
              Cancel
            </Button>
            {bundleDraft.serial_prefix && (
              <span
                className="pb-3 text-[11px] text-zinc-500"
                data-testid="bundle-serial-preview"
              >
                Serials: {bundleDraft.serial_prefix}
                {String(Number(bundleDraft.serial_start) || 1).padStart(
                  2,
                  "0",
                )}{" "}
                → {bundleDraft.serial_prefix}
                {String(
                  (Number(bundleDraft.serial_start) || 1) +
                    (Number(bundleDraft.count) || 1) -
                    1,
                ).padStart(2, "0")}
              </span>
            )}
          </div>
        </div>
      )}

      {adding && (
        <div
          className="mt-3 grid gap-2 rounded-md border border-zinc-800 bg-zinc-950 p-3 lg:grid-cols-3"
          data-testid="slab-piece-form"
        >
          {[
            ["name", "Name"],
            ["serial_number", "Serial #"],
            ["lot_number", "Lot #"],
            ["bundle_number", "Bundle #"],
            ["length_in", "Length (in)"],
            ["width_in", "Width (in)"],
          ].map(([k, label]) => (
            <div key={k} className="space-y-1">
              <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                {label}
              </span>
              <Input
                data-testid={`piece-field-${k}`}
                type={k.endsWith("_in") ? "number" : "text"}
                value={draft[k]}
                onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                className="h-11 border-zinc-800 bg-zinc-900"
              />
            </div>
          ))}
          <div className="flex items-end gap-2 lg:col-span-3">
            <Button
              data-testid="save-slab-piece-button"
              disabled={busy}
              onClick={add}
              className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              {busy ? "Saving…" : "Add piece"}
            </Button>
            <Button
              data-testid="cancel-slab-piece-button"
              variant="outline"
              onClick={() => setAdding(false)}
              className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {pieces.length === 0 ? (
        <p className="mt-3 text-sm text-zinc-500">
          No individual pieces logged yet. Pieces are added automatically when
          this material is received on a purchase order, or add one by hand.
        </p>
      ) : view === "grid" ? (
        <div
          className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
          data-testid="pieces-grid"
        >
          {pieces.map((p) => {
            const cover = coverOf(p);
            return (
              <div
                key={p.id}
                data-testid={`piece-tile-${p.id}`}
                className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950"
              >
                <div className="aspect-[4/3] w-full bg-zinc-900">
                  {cover ? (
                    <img
                      src={photoSrc(cover)}
                      alt={p.name || slab.slab_color}
                      data-testid={`piece-tile-image-${p.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setZoom({
                          src: photoSrc(cover),
                          label: p.name || slab.slab_color,
                        });
                      }}
                      className="h-full w-full cursor-zoom-in object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-[10px] uppercase tracking-widest text-zinc-600">
                      No photo
                    </div>
                  )}
                </div>
                <div className="space-y-2 p-3">
                  <div className="truncate text-sm font-semibold">
                    {p.name || slab.slab_color}
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500">
                    {p.serial_number && (
                      <span className="font-mono-data rounded-full border border-zinc-800 px-2 py-0.5">
                        SN {p.serial_number}
                      </span>
                    )}
                    <span className="font-mono-data rounded-full border border-yellow-500/25 bg-yellow-500/10 px-2 py-0.5 text-yellow-400">
                      {sqftOf(p, slab)} ft² each
                    </span>
                    {heldChip(p)}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {STATUSES.map((st) => (
                      <button
                        key={st}
                        data-testid={`piece-tile-set-${st.toLowerCase().replace(/ /g, "-")}-${p.id}`}
                        onClick={() => setStatus(p.id, st)}
                        className={`rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-widest transition-colors duration-150 ${
                          (p.status || "In Stock") === st
                            ? STATUS_STYLE[st]
                            : "border-zinc-800 bg-zinc-900 text-zinc-600 hover:text-zinc-300"
                        }`}
                      >
                        {st}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Button
                      data-testid={`piece-tile-edit-${p.id}`}
                      variant="outline"
                      onClick={() =>
                        editing === p.id ? setEditing(null) : startEdit(p)
                      }
                      className="h-8 border-zinc-700 bg-transparent px-2.5 text-[10px] uppercase tracking-widest hover:bg-zinc-800"
                    >
                      <PencilSimple size={12} weight="bold" className="mr-1" />{" "}
                      Edit
                    </Button>
                    <Button
                      data-testid={`piece-tile-label-${p.id}`}
                      variant="outline"
                      onClick={() => printLabels([p])}
                      className="h-8 border-zinc-700 bg-transparent px-2.5 text-[10px] uppercase tracking-widest hover:bg-zinc-800"
                    >
                      <Printer size={12} weight="bold" className="mr-1" /> Label
                    </Button>
                    {holdButton(p, true)}
                    <Button
                      data-testid={`piece-tile-photo-${p.id}`}
                      variant="outline"
                      disabled={busy}
                      title="Open the camera and snap a photo"
                      onClick={() => pickFor(p.id, fileRef)}
                      className="h-8 border-zinc-700 bg-transparent px-2.5 text-[10px] uppercase tracking-widest hover:bg-zinc-800"
                    >
                      <Camera size={12} weight="bold" className="mr-1" /> Camera
                    </Button>
                    <Button
                      data-testid={`piece-tile-upload-${p.id}`}
                      variant="outline"
                      disabled={busy}
                      title="Pick a photo from your library or files"
                      onClick={() => pickFor(p.id, libraryRef)}
                      className="h-8 border-zinc-700 bg-transparent px-2.5 text-[10px] uppercase tracking-widest hover:bg-zinc-800"
                    >
                      <UploadSimple size={12} weight="bold" className="mr-1" />{" "}
                      Upload
                    </Button>
                    <button
                      data-testid={`piece-tile-delete-${p.id}`}
                      onClick={() => remove(p.id)}
                      className="ml-auto text-zinc-500 hover:text-red-400"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                  {editing === p.id && editFields(p.id)}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {pieces.map((p) => (
            <div
              key={p.id}
              data-testid={`slab-piece-${p.id}`}
              className="relative overflow-hidden rounded-md border border-zinc-800 bg-zinc-950 p-3 pl-4"
            >
              <span className="absolute left-0 top-0 h-full w-1 bg-indigo-500" />
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1 truncate text-sm font-semibold">
                      {p.name || slab.slab_color}
                    </div>
                    <span
                      data-testid={`piece-sqft-${p.id}`}
                      className="font-mono-data shrink-0 rounded-full border border-yellow-500/25 bg-yellow-500/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-yellow-400"
                    >
                      {sqftOf(p, slab)} ft² each
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500">
                    {p.serial_number && (
                      <span className="font-mono-data rounded-full border border-zinc-800 px-2 py-0.5">
                        SN {p.serial_number}
                      </span>
                    )}
                    {p.lot_number && (
                      <span className="font-mono-data rounded-full border border-zinc-800 px-2 py-0.5">
                        Lot {p.lot_number}
                      </span>
                    )}
                    {p.bundle_number && (
                      <span className="font-mono-data rounded-full border border-zinc-800 px-2 py-0.5">
                        Bundle {p.bundle_number}
                      </span>
                    )}
                    <span className="font-mono-data rounded-full border border-zinc-800 px-2 py-0.5">
                      {p.length_in}&quot; x {p.width_in}&quot;
                    </span>
                    {p.source && (
                      <span className="rounded-full border border-indigo-400/25 bg-indigo-500/10 px-2 py-0.5 text-indigo-300">
                        {p.source}
                      </span>
                    )}
                    {heldChip(p)}
                  </div>
                  <div className="relative mt-2 after:pointer-events-none after:absolute after:right-0 after:top-0 after:h-full after:w-8 after:bg-gradient-to-l after:from-zinc-950 after:to-transparent lg:after:hidden">
                    <div
                      className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:flex-wrap"
                      data-testid={`piece-status-${p.id}`}
                    >
                      {STATUSES.map((st) => (
                        <button
                          key={st}
                          data-testid={`piece-set-${st.toLowerCase().replace(/ /g, "-")}-${p.id}`}
                          onClick={() => setStatus(p.id, st)}
                          className={`shrink-0 whitespace-nowrap rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-widest transition-colors duration-150 ${
                            (p.status || "In Stock") === st
                              ? STATUS_STYLE[st]
                              : "border-zinc-800 bg-zinc-900 text-zinc-600 hover:text-zinc-300"
                          }`}
                        >
                          {st}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div
                  className="flex flex-wrap items-center gap-2 border-t border-zinc-800/80 pt-3 lg:shrink-0 lg:border-t-0 lg:pt-0"
                  data-testid={`piece-actions-${p.id}`}
                >
                  <Button
                    data-testid={`piece-edit-button-${p.id}`}
                    variant="outline"
                    onClick={() =>
                      editing === p.id ? setEditing(null) : startEdit(p)
                    }
                    className="h-9 border-zinc-700 bg-transparent px-2.5 text-[10px] uppercase tracking-widest hover:bg-zinc-800 sm:h-10 sm:px-3 sm:text-xs"
                  >
                    <PencilSimple size={14} weight="bold" className="mr-1" />{" "}
                    Edit
                  </Button>
                  <Button
                    data-testid={`piece-label-button-${p.id}`}
                    variant="outline"
                    onClick={() => printLabels([p])}
                    className="h-9 border-zinc-700 bg-transparent px-2.5 text-[10px] uppercase tracking-widest hover:bg-zinc-800 sm:h-10 sm:px-3 sm:text-xs"
                  >
                    <Printer size={14} weight="bold" className="mr-1" /> Label
                  </Button>
                  {p.bundle_number && (
                    <Button
                      data-testid={`piece-bundle-label-${p.id}`}
                      variant="outline"
                      onClick={() => printBundle(p)}
                      className="h-9 border-indigo-400/40 bg-indigo-500/10 px-2.5 text-[10px] uppercase tracking-widest text-indigo-300 hover:bg-indigo-500/20 sm:h-10 sm:px-3 sm:text-xs"
                    >
                      Bundle {p.bundle_number}
                    </Button>
                  )}
                  {holdButton(p, false)}
                  <Button
                    data-testid={`piece-photo-button-${p.id}`}
                    variant="outline"
                    disabled={busy}
                    title="Open the camera and snap a photo"
                    onClick={() => pickFor(p.id, fileRef)}
                    className="h-9 border-zinc-700 bg-transparent px-2.5 text-[10px] uppercase tracking-widest hover:bg-zinc-800 sm:h-10 sm:px-3 sm:text-xs"
                  >
                    <Camera size={14} weight="bold" className="mr-1" /> Camera
                  </Button>
                  <Button
                    data-testid={`piece-upload-button-${p.id}`}
                    variant="outline"
                    disabled={busy}
                    title="Pick a photo from your library or files"
                    onClick={() => pickFor(p.id, libraryRef)}
                    className="h-9 border-zinc-700 bg-transparent px-2.5 text-[10px] uppercase tracking-widest hover:bg-zinc-800 sm:h-10 sm:px-3 sm:text-xs"
                  >
                    <UploadSimple size={14} weight="bold" className="mr-1" />{" "}
                    Upload
                  </Button>
                  <button
                    data-testid={`piece-delete-${p.id}`}
                    onClick={() => remove(p.id)}
                    title="Remove piece"
                    className="ml-auto flex h-9 w-9 items-center justify-center rounded-md text-zinc-500 transition-colors duration-150 hover:bg-red-950/40 hover:text-red-400 lg:ml-0"
                  >
                    <Trash size={15} weight="bold" />
                  </button>
                </div>
              </div>
              {editing === p.id && editFields(p.id)}
              {livePhotos(p).length > 0 && (
                <div data-testid={`piece-photos-${p.id}`}>
                  <PhotoGallery
                    photos={livePhotos(p)}
                    onRemove={(photoId) => removePhoto(p.id, photoId)}
                    onEdit={(ph) => editPhoto(p.id, ph)}
                    onReorder={(order) => reorderPhotos(p.id, order)}
                    onToggleWebsite={(ph) => toggleWebsite(p.id, ph)}
                    onRevertHd={(ph) => revertHd(p.id, ph)}
                    testPrefix={`piece-photo-${p.id}`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {zoom && (
        <div
          data-testid="piece-photo-lightbox"
          onClick={() => setZoom(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
        >
          <img
            src={zoom.src}
            alt={zoom.label}
            className="max-h-[90vh] max-w-full rounded-lg border border-zinc-800 object-contain"
          />
          <button
            data-testid="piece-photo-lightbox-close"
            onClick={() => setZoom(null)}
            className="absolute right-4 top-4 rounded-full border border-zinc-700 bg-zinc-900/80 p-2 text-zinc-300 hover:text-white"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
      )}
      <input
        ref={fileRef}
        data-testid="piece-photo-input"
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={upload}
      />
      <input
        ref={libraryRef}
        data-testid="piece-library-input"
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={upload}
      />
      {pendingEdit && (
        <PhotoEditor
          file={pendingEdit.file}
          onDone={saveEdited}
          onClose={() => setPendingEdit(null)}
        />
      )}
      {holdFor && (
        <PieceHoldDialog
          slab={slab}
          piece={holdFor}
          open={!!holdFor}
          onClose={() => setHoldFor(null)}
          onSlab={onChange}
        />
      )}
      {printing && (
        <div
          id="piece-print-area"
          className="mt-4"
          data-testid="piece-label-print-area"
        >
          <div className="no-print mb-2 flex items-center justify-between gap-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              Label preview — {printing.length} label
              {printing.length === 1 ? "" : "s"} · 4 x 6 in
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="piece-label-print-again"
                onClick={() => printLabels(printing)}
                className="h-9 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                <Printer size={13} weight="bold" className="mr-1" /> Print
              </Button>
              <Button
                data-testid="piece-label-close"
                variant="outline"
                onClick={() => setPrinting(null)}
                className="h-9 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest hover:bg-zinc-800"
              >
                Close
              </Button>
            </div>
          </div>
          <div className="label-stack space-y-4">
            {printing.map((p) => (
              <div
                key={p.id}
                data-testid={`piece-label-wrap-${p.id}`}
                className="origin-top-left scale-[0.72] sm:scale-100"
                style={{ width: "4in" }}
              >
                <PieceLabel slab={slab} piece={p} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
